/**
 * Angular `property-edit` / `property-add` `notifyError(error)` uses HttpClient's `error.error`:
 * `{ message: string, data: Array<{ name: string; id: number }> }` when
 * `message` includes `property with this address already`.
 *
 * React's axios interceptor rejects with `{ message, status, errors?, data?: fullResponseBody }`
 * where `data` is the same object Angular saw as `error.error`.
 * Raw `AxiosError` still has `error.response.data` as that body.
 */
import { DUPLICATE_MARKER } from '../constants'
import type { DuplicatePropertyRow } from '../types'

function getResponseBody(err: unknown): Record<string, unknown> | null {
  if (err === null || typeof err !== 'object') return null
  const e = err as Record<string, unknown>

  // Shape after response interceptor: full API JSON is on `data`
  const wrapped = e.data
  if (wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped)) {
    return wrapped as Record<string, unknown>
  }

  // Raw Axios error before / without interceptor normalization
  const response = e.response as { data?: unknown } | undefined
  const rd = response?.data
  if (rd && typeof rd === 'object' && !Array.isArray(rd)) {
    return rd as Record<string, unknown>
  }

  return null
}

function combinedMessage(err: unknown, body: Record<string, unknown> | null): string {
  const parts: string[] = []
  if (err !== null && typeof err === 'object') {
    const m = (err as Record<string, unknown>).message
    if (typeof m === 'string' && m.trim()) parts.push(m)
  }
  if (body && typeof body.message === 'string' && body.message.trim()) {
    parts.push(body.message)
  }
  return parts.join(' ')
}

function extractDuplicateRows(body: Record<string, unknown> | null): DuplicatePropertyRow[] {
  if (!body) return []
  const raw = body.data
  if (!Array.isArray(raw) || raw.length === 0) return []

  const rows: DuplicatePropertyRow[] = []
  for (const item of raw) {
    if (item === null || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    const id = o.id ?? o.property_id
    if (id === undefined || id === null) continue
    rows.push({
      name: o.name != null ? String(o.name) : String(id),
      id: typeof id === 'number' || typeof id === 'string' ? id : String(id),
    })
  }
  return rows
}

/**
 * If this API error is the duplicate-address case, returns the property rows for the confirm dialog.
 * Otherwise returns null.
 */
export function parseDuplicateAddressError(err: unknown): DuplicatePropertyRow[] | null {
  const body = getResponseBody(err)
  const haystack = combinedMessage(err, body).toLowerCase()
  if (!haystack.includes(DUPLICATE_MARKER)) return null

  const rows = extractDuplicateRows(body)
  return rows.length > 0 ? rows : null
}
